/** Personal home-page widgets (backlog #28). The catalogue, layout rules and per-role defaults live here so the API and web agree. */

export type WidgetSize = 's' | 'm' | 'l';
export type WidgetKey =
  | 'summary' | 'announcements' | 'acks' | 'tasks' | 'unread' | 'clock' | 'shifts' | 'birthdays' | 'kudos' | 'handovers'
  | 'checklists' | 'incidents' | 'onboarding' | 'agenda' | 'activity' | 'projects' | 'kpi_hours' | 'kpi_incidents'
  | 'sales_takings' | 'sales_transactions' | 'sales_basket' | 'sales_top' | 'todos' | 'training' | 'hr_approvals';

export interface WidgetDef {
  key: WidgetKey;
  title: string;
  description: string;
  sizes: WidgetSize[];
  default_size: WidgetSize;
  /** Shown only to people holding this permission (anywhere). */
  permission?: string;
  /** Shown only to people on the rota. */
  employee_only?: boolean;
  /**
   * t361u: the day the widget was added (YYYY-MM-DD). Layouts saved before then get it once, after `after` (see
   * withNewWidgets), so a new act-on-it widget reaches people who customised Home; removing it afterwards sticks.
   */
  added_on?: string;
  after?: WidgetKey;
}

export const WIDGETS: WidgetDef[] = [
  { key: 'summary', title: 'At a glance', description: 'Unread, tasks, next shift and notifications', sizes: ['l'], default_size: 'l' },
  { key: 'announcements', title: 'Announcements', description: 'Latest pinned and unread announcements', sizes: ['m', 'l'], default_size: 'l' },
  { key: 'acks', title: 'Acknowledgements needed', description: 'Policies and announcements waiting for you', sizes: ['s', 'm', 'l'], default_size: 'm' },
  { key: 'tasks', title: 'My tasks', description: 'Your open tasks, private ones included, with quick add for a private task', sizes: ['s', 'm', 'l'], default_size: 'm' },
  { key: 'unread', title: 'Unread conversations', description: 'Channels and DMs with new messages', sizes: ['s', 'm', 'l'], default_size: 'm' },
  { key: 'clock', title: 'Time clock', description: 'Clock in and out, breaks', sizes: ['s', 'm'], default_size: 's', employee_only: true },
  { key: 'shifts', title: 'My shifts', description: 'Your next published shifts', sizes: ['s', 'm'], default_size: 's', employee_only: true },
  { key: 'handovers', title: 'Handovers', description: 'Handovers from the last shift to read', sizes: ['s', 'm'], default_size: 's', employee_only: true },
  { key: 'checklists', title: 'Checklists due', description: 'Checklists assigned to you or your building', sizes: ['s', 'm'], default_size: 's' },
  { key: 'incidents', title: 'Open issues', description: 'Open incidents and maintenance at your buildings', sizes: ['s', 'm', 'l'], default_size: 'm' },
  { key: 'training', title: 'My training', description: 'Training you need to complete, with due dates', sizes: ['s', 'm'], default_size: 's', added_on: '2026-10-25', after: 'acks' },
  // t413u: HR changes people asked for, waiting for this person's approval (only for hr.edit holders; the HR records module hides it when HR is off).
  { key: 'hr_approvals', title: 'HR changes to approve', description: 'Changes to HR details waiting for your approval', sizes: ['s', 'm'], default_size: 's', permission: 'hr.edit', added_on: '2026-10-27', after: 'tasks' },
  { key: 'onboarding', title: 'Onboarding', description: 'Your onboarding progress', sizes: ['s', 'm'], default_size: 's' },
  { key: 'agenda', title: 'Agenda', description: 'Your calendar for the next 7 days', sizes: ['s', 'm'], default_size: 's' },
  { key: 'birthdays', title: 'Birthdays', description: 'Colleagues celebrating soon', sizes: ['s', 'm'], default_size: 's' },
  { key: 'kudos', title: 'Kudos', description: 'Recent kudos', sizes: ['s', 'm'], default_size: 's' },
  { key: 'activity', title: 'Recent activity', description: 'Your latest notifications', sizes: ['s', 'm'], default_size: 's' },
  { key: 'projects', title: 'Projects', description: 'Shortcuts to your projects', sizes: ['s', 'm', 'l'], default_size: 's' },
  { key: 'kpi_hours', title: 'Hours this week', description: 'Scheduled vs worked hours for buildings you manage', sizes: ['s', 'm'], default_size: 's', permission: 'workforce.reports.view' },
  { key: 'kpi_incidents', title: 'Issues KPI', description: 'Open, overdue and new issues', sizes: ['s', 'm'], default_size: 's', permission: 'ops.incident.manage' },
  // Backlog #30: Odoo till sales for the person's building (server re-checks sales.view at that building).
  { key: 'sales_takings', title: "Today's takings", description: 'Takings so far today against the target; every building you can see when there are several', sizes: ['s', 'm', 'l'], default_size: 'l', permission: 'sales.view' },
  { key: 'sales_transactions', title: 'Transactions', description: 'Till transactions today, by hour', sizes: ['s', 'm'], default_size: 's', permission: 'sales.view' },
  { key: 'sales_basket', title: 'Average basket', description: 'Average spend per transaction today', sizes: ['s'], default_size: 's', permission: 'sales.view' },
  { key: 'sales_top', title: 'Top sellers', description: "Today's best-selling products", sizes: ['s', 'm'], default_size: 's', permission: 'sales.view' },
];
export const WIDGET_BY_KEY = new Map(WIDGETS.map(w => [w.key, w]));
/** Retired keys and what they became: "My to-dos" merged into "My tasks" when to-dos became private tasks. */
export const WIDGET_ALIASES: Partial<Record<string, WidgetKey>> = { todos: 'tasks' };
export const MAX_WIDGETS = 24;

/** col (t131u): which side rail of the three-column Home it sits in; missing means "pick by widget" (railFor). */
export type RailSide = 'left' | 'right';
export interface LayoutItem { key: WidgetKey; size: WidgetSize; col?: RailSide }
export interface HomeLayout {
  items: LayoutItem[];
  /** Where the layout came from: the person's own, their role's default, or the built-in default. */
  source: 'mine' | 'role' | 'default';
  role_key: string | null;
  /** Widgets this person may add (permission-checked). */
  available: WidgetKey[];
  /** The saved template last applied, while the layout still matches it. */
  active_template_id?: string | null;
}

/** Columns (of 3) a size spans. */
export const SIZE_SPAN: Record<WidgetSize, number> = { s: 1, m: 2, l: 3 };
export const SIZE_LABEL: Record<WidgetSize, string> = { s: 'Small', m: 'Medium', l: 'Wide' };

/** Drops unknown, duplicate and not-allowed widgets; snaps sizes to the widget's allowed ones. */
export function normaliseLayout(items: readonly { key: string; size?: string; col?: string }[], allowed?: ReadonlySet<string>): LayoutItem[] {
  const seen = new Set<string>();
  const out: LayoutItem[] = [];
  for (const it of items) {
    const def = WIDGET_BY_KEY.get((WIDGET_ALIASES[it.key] ?? it.key) as WidgetKey);
    if (!def || seen.has(def.key) || (allowed && !allowed.has(def.key))) continue;
    seen.add(def.key);
    const size = def.sizes.includes(it.size as WidgetSize) ? (it.size as WidgetSize) : def.default_size;
    out.push({ key: def.key, size, ...(it.col === 'left' || it.col === 'right' ? { col: it.col } : {}) });
    if (out.length >= MAX_WIDGETS) break;
  }
  return out;
}

/**
 * t361u: adds widgets introduced after a layout was saved (`savedAt`, an ISO time) that it doesn't have, each after
 * its `after` widget (else at the end), on that widget's rail. Only widgets in `allowed` (when given) are added.
 */
export function withNewWidgets(items: LayoutItem[], savedAt: string | null | undefined, allowed?: ReadonlySet<string>): LayoutItem[] {
  if (!savedAt) return items;
  const day = savedAt.slice(0, 10);
  let out = [...items];
  for (const w of WIDGETS) {
    if (out.length >= MAX_WIDGETS) break;
    if (!w.added_on || day >= w.added_on || out.some(i => i.key === w.key) || (allowed && !allowed.has(w.key))) continue;
    const at = w.after ? out.findIndex(i => i.key === w.after) : -1;
    const item: LayoutItem = { key: w.key, size: w.default_size, ...(at >= 0 && out[at]!.col ? { col: out[at]!.col } : {}) };
    out = at >= 0 ? [...out.slice(0, at + 1), item, ...out.slice(at + 1)] : [...out, item];
  }
  return out;
}

const L = (...keys: WidgetKey[]): LayoutItem[] => keys.map(k => ({ key: k, size: WIDGET_BY_KEY.get(k)!.default_size }));

/** Built-in defaults per preset role, used until an admin saves one or the person edits theirs. */
export const BUILTIN_DEFAULTS: Record<string, LayoutItem[]> = {
  everyone: L('summary', 'announcements', 'acks', 'training', 'clock', 'shifts', 'tasks', 'unread', 'agenda', 'birthdays', 'activity'),
  employee: L('summary', 'announcements', 'clock', 'shifts', 'acks', 'training', 'handovers', 'checklists', 'onboarding', 'tasks', 'birthdays', 'kudos'),
  supervisor: L('summary', 'announcements', 'clock', 'handovers', 'checklists', 'incidents', 'acks', 'training', 'tasks', 'shifts', 'kpi_hours'),
  workforce_manager: L('summary', 'kpi_hours', 'kpi_incidents', 'announcements', 'incidents', 'acks', 'training', 'tasks', 'agenda', 'unread'),
  manager: L('summary', 'kpi_hours', 'kpi_incidents', 'announcements', 'incidents', 'tasks', 'acks', 'training', 'agenda', 'unread', 'projects'),
  admin: L('summary', 'kpi_hours', 'kpi_incidents', 'announcements', 'incidents', 'tasks', 'hr_approvals', 'acks', 'training', 'agenda', 'unread', 'projects'),
};
/** Order used to pick the role whose default applies when someone holds several. */
export const ROLE_PRIORITY = ['owner', 'admin', 'manager', 'workforce_admin', 'workforce_manager', 'supervisor', 'employee', 'member'];
export const DEFAULT_ROLE_KEYS = ['everyone', 'employee', 'supervisor', 'workforce_manager', 'manager', 'admin'] as const;
export const ROLE_KEY_LABEL: Record<string, string> = {
  everyone: 'Everyone else', employee: 'Employees', supervisor: 'Supervisors', workforce_manager: 'Workforce managers', manager: 'Managers', admin: 'Admins and owners',
};
/** Maps a preset system key onto a default-layout key. */
export function defaultsKeyFor(systemKeys: readonly string[]): string {
  const best = ROLE_PRIORITY.find(k => systemKeys.includes(k));
  if (!best) return 'everyone';
  if (best === 'owner' || best === 'admin') return 'admin';
  if (best === 'workforce_admin') return 'workforce_manager';
  if (best === 'member') return 'everyone';
  return best;
}

/** Moves the item at `from` to index `to` (drag-and-drop and the keyboard Move buttons). */
export function moveItem<T>(list: readonly T[], from: number, to: number): T[] {
  const out = [...list];
  if (from < 0 || from >= out.length) return out;
  const [x] = out.splice(from, 1);
  out.splice(Math.max(0, Math.min(to, out.length)), 0, x);
  return out;
}

/** A saved, named home layout (backlog #29). Owners can share one with a team or a building. */
export interface HomeTemplate {
  id: string;
  name: string;
  items: LayoutItem[];
  owner_id: string;
  owner_name: string;
  shared_with: { type: 'team' | 'location'; id: string; name: string } | null;
  mine: boolean;
  active: boolean;
  updated_at: string;
}
export const MAX_TEMPLATES = 20;

/** t131u: personal, act-on-it widgets default to the left rail; awareness widgets to the right. */
const LEFT_DEFAULT = new Set<WidgetKey>(['summary', 'clock', 'shifts', 'tasks', 'acks', 'training', 'agenda', 'checklists', 'handovers', 'onboarding', 'unread', 'hr_approvals']);
export const railFor = (it: LayoutItem): RailSide => it.col ?? (LEFT_DEFAULT.has(it.key) ? 'left' : 'right');
/** Splits a layout into its two rails, keeping saved order within each. */
export function splitRails(items: LayoutItem[]): Record<RailSide, LayoutItem[]> {
  return { left: items.filter(i => railFor(i) === 'left'), right: items.filter(i => railFor(i) === 'right') };
}
/** Rejoins rails into one saved list (left first), stamping each item's side so a move between rails sticks. */
export const joinRails = (r: Record<RailSide, LayoutItem[]>): LayoutItem[] => [...r.left.map(i => ({ ...i, col: 'left' as const })), ...r.right.map(i => ({ ...i, col: 'right' as const }))];
